import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../components/PublicLayout';
import { Eyebrow, FeatureExplorer, HomeCta } from '../components/marketing';
import { marketingLinks } from '../constants/marketingEvent';
import {
  marketingColor,
  marketingFont,
  marketingLayout,
  marketingLine,
  marketingLogoPlateSx,
} from '../theme/marketingTokens';

/**
 * Channels the platform pushes to.
 *
 * Logos are the copies under `public/logos/platforms/`, not hotlinks. They
 * come in their own brand colours and several are dark marks (BigCommerce,
 * Lightspeed), so each sits on a light plate rather than being recoloured.
 * The last two have no mark to show and keep the handoff's outline chip.
 */
const CHANNEL_LOGOS = [
  { name: 'Shopify', src: '/logos/platforms/shopify.svg' },
  { name: 'BigCommerce', src: '/logos/platforms/bigcommerce.svg' },
  { name: 'WooCommerce', src: '/logos/platforms/woocommerce.svg' },
  { name: 'Lightspeed', src: '/logos/platforms/lightspeed.svg' },
];


export const FeaturesPage: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const bookDemo = () => window.open(marketingLinks.scheduler, '_blank', 'noopener,noreferrer');

  return (
    <PublicLayout>
      <Box>
        <Box
          component="section"
          sx={{
            maxWidth: 800,
            mx: 'auto',
            px: marketingLayout.sectionPaddingX,
            pt: { xs: 7, md: 12 },
            pb: { xs: 4, md: 8 },
            textAlign: 'center',
          }}
        >
          <Eyebrow sx={{ mb: 3.25 }}>{t('public.features.eyebrow')}</Eyebrow>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.25rem', md: '2.75rem' }, mb: 2.25 }}>
            {t('public.features.title')}
          </Typography>
          <Typography sx={{ fontSize: 16, color: marketingColor.textSecondary, lineHeight: 1.65 }}>
            {t('public.features.subtitle')}
          </Typography>
        </Box>

        <Box
          component="section"
          sx={{
            maxWidth: marketingLayout.maxWidth,
            mx: 'auto',
            px: marketingLayout.sectionPaddingX,
            pt: 2.5,
            pb: { xs: 8, md: 12 },
          }}
        >
          <FeatureExplorer />
        </Box>

        <Box
          component="section"
          sx={{
            maxWidth: marketingLayout.maxWidth,
            mx: 'auto',
            px: marketingLayout.sectionPaddingX,
            py: { xs: 7, md: 10.5 },
            textAlign: 'center',
            borderTop: `1px solid ${marketingLine.hairline}`,
          }}
        >
          <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', md: '1.625rem' }, mb: 3.75 }}>
            {t('public.features.channels.title')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mb: 3.5,
            }}
          >
            {CHANNEL_LOGOS.map((channel) => (
              <Box
                key={channel.name}
                sx={{ ...marketingLogoPlateSx, px: 2.5, py: 1.75, minWidth: 160, height: 64 }}
              >
                <Box
                  component="img"
                  src={channel.src}
                  alt={channel.name}
                  loading="lazy"
                  sx={{ maxWidth: '100%', maxHeight: 30, width: 'auto', height: 'auto' }}
                />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.25 }}>
            {(t('public.features.channelChips', { returnObjects: true }) as string[]).map((channel) => (
              <Box
                key={channel}
                sx={{
                  border: `1px solid ${marketingLine.hairlineStrong}`,
                  borderRadius: '3px',
                  px: 2.5,
                  py: 1.4,
                  fontFamily: marketingFont.mono,
                  fontSize: 13,
                  color: marketingColor.accent,
                }}
              >
                {channel}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ borderTop: `1px solid ${marketingLine.hairline}` }}>
          <HomeCta onBookDemo={bookDemo} />
        </Box>
      </Box>
    </PublicLayout>
  );
};

export default FeaturesPage;
